import React, { useEffect } from "react";
import {
  Autocomplete,
  Box,
  Chip,
  Icon,
  Paper,
  Stack,
  TextField,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import { ProcessedItemData } from "../ProcessedItemFormDialog";
import { facilitiesService } from "../../../../services/facilities.service";
import { toast } from "react-toastify";

interface StorageStepProps {
  fullDocumnet: any;

  handleUpdateFullDoc: (value: any, field: string) => void;
}

export const StorageStep: React.FC<StorageStepProps> = ({
  fullDocumnet,
  handleUpdateFullDoc,
}) => {
  const [loading, setLoading] = React.useState(false);
  const theme = useTheme();
  const [facilities, setFacilities] = React.useState<{
    branches: any[];
    warehouses: any[];
  }>({
    branches: [],
    warehouses: [],
  });

  const loadFacilities = () => {
    setLoading(true);
    Promise.all([
      facilitiesService.listBranches({}),
      facilitiesService.listWarehouses({}),
    ])
      .then((res) => {
        const branchesResponse = res[0];
        const warehousesResponse = res[1];

        setFacilities({
          branches: branchesResponse.data,
          warehouses: warehousesResponse.data,
        });
      })
      .catch(() => {
        toast.error("Failed to load required data");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(loadFacilities, []);

  const getFacilityOptions = () => {
    const options = [];

    for (const branch of facilities.branches) {
      options.push({
        id: branch.id,
        name: branch.name,
        type: "branch",
        label: branch.name,
        details: "Branch",
      });
    }

    for (const warehouse of facilities.warehouses) {
      options.push({
        id: warehouse._id,
        name: warehouse.name,
        type: "warehouse",
        label: warehouse.name,
        details: "Warehouse",
      });
    }

    return options;
  };

  return (
    <Paper elevation={0} variant="outlined" sx={{ p: 2 }}>
      {loading ? null : (
        <>
          <Typography
            variant="subtitle2"
            gutterBottom
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              color: "primary.main",
              fontWeight: 500,
            }}
          >
            <Icon fontSize="small">warehouse</Icon>
            Storage Location
          </Typography>
          <Stack spacing={2}>
            <Autocomplete
              options={getFacilityOptions()}
              getOptionLabel={(option) => option.name}
              value={
                getFacilityOptions().find(
                  (f) => f.id === fullDocumnet.storage_place
                ) || null
              }
              onChange={(_, value) => {
                handleUpdateFullDoc(value ? value.id : "", "storage_place");
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Storage Location"
                  required
                  placeholder="Select storage location..."
                  helperText="Where the processed items will be stored"
                />
              )}
              renderOption={(props, option) => (
                <Box
                  component="li"
                  {...props}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Icon fontSize="small">
                      {option.type === "warehouse" ? "warehouse" : "store"}
                    </Icon>
                    <Typography>{option.name}</Typography>
                  </Box>
                  <Chip
                    size="small"
                    label={option.details}
                    sx={{
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                    }}
                  />
                </Box>
              )}
            />

            <TextField
              fullWidth
              label="Storage Notes"
              multiline
              rows={3}
              value={fullDocumnet.storage_notes}
              onChange={(e) =>
                handleUpdateFullDoc(e.target.value, "storage_notes")
              }
              placeholder="Add any special storage instructions or notes..."
              helperText="Optional: Add storage instructions or special handling notes"
            />
          </Stack>
        </>
      )}
    </Paper>
  );
};
